import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ACCOUNT_TABS, ADDRESS_TABS, CERTIFICATE_TABS, formatWibLong, syncScopeOf, tabSection, ITEM_TABS, MASTER_TABS, UNIT_TABS, USER_TAB_RIGHT, USER_TABS, PRESET_LABEL, landingTab, PRESETS, TAB_LABEL, type Preset, type Tab } from "@po/shared";
import { api, type Freshness } from "../lib/api";
import { ago } from "../lib/format";
import { ProgressBar } from "./ProgressBar";
import { readPrefs, writePrefs } from "../lib/prefs";
import { usePageSyncState } from "../lib/page-sync";
import { useSyncNow } from "../lib/sync-now";
import { applyTheme, getTheme, type Theme } from "../lib/theme";
import { presetDefaults, useDashboard } from "../pages/Dashboard";

export function useNow(intervalMs = 15_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** "Updated 3 min ago", ticking in the browser; hover shows the WIB time (SPEC §11.1). */
export function FreshnessIndicator({ f }: { f?: Freshness }) {
  const now = useNow();
  if (!f) return null;
  if (!f.asOf) return <span className="text-[12px] text-red-200">No successful sync yet</span>;
  const at = new Date(f.asOf);
  const rel = ago(now - at.getTime());
  const stale = f.level !== "fresh";
  return (
    <span title={formatWibLong(at)} tabIndex={0} className={`text-[12px] ${stale ? "text-amber-300 font-semibold" : "text-slate-300"}`}>
      {stale ? (
        <>
          <span aria-hidden>⚠ </span>Data may be out of date — last updated {rel}
        </>
      ) : (
        <>{f.scheduled ? "Updated" : "Last synced"} {rel}</>
      )}
    </span>
  );
}

/** Procurement holds the four record tabs; Budgeting is the Budgets tab; Address Book the address book (SPEC §6.1). */
export const PROCUREMENT_TABS: Tab[] = ["overview", "requisitions", "rfqs", "pos", "spend"];

/** A menu entry Sertica has that the dashboard cannot read: shown greyed, with why. */
type Locked = { label: string; why: string };
type Group = { key: string; label: string; icon: JSX.Element; tabs: Tab[]; entries?: (Tab | Locked)[] };

const entryLabel = (e: Tab | Locked) => (typeof e === "string" ? TAB_LABEL[e] : e.label);
/** The menu is alphabetical, sections and their entries; a lock sign does not count. */
export const byLabel = <T extends Tab | Locked>(xs: readonly T[]): T[] => [...xs].sort((a, b) => entryLabel(a).localeCompare(entryLabel(b)));
const sortGroup = (g: Group): Group => ({ ...g, tabs: byLabel(g.tabs), entries: g.entries && byLabel(g.entries) });

/** Address Book Info (SPEC §8.7). CSR and Product Group have no API. */
const ADDRESS_ENTRIES: (Tab | Locked)[] = [
  "addresses",
  "address-categories",
  "address-classifications",
  "country-codes",
  { label: "CSR", why: "Sertica's Web API has no CSR list" },
  "delivery-conditions",
  "framework-agreements",
  "payment-conditions",
  { label: "Product Group", why: "Sertica's Web API has no product group list" },
];

/** Item Info (SPEC §8.8). Catalog has no API; Item Type and Tax Code answer 403. */
const ITEM_ENTRIES: (Tab | Locked)[] = [
  { label: "Catalog", why: "Sertica's Web API has no catalog" },
  "items",
  "item-groups",
  "item-tree",
  { label: "Item Type", why: "The service account lacks the right to read item types" },
  "measuring-units",
  { label: "Tax Code", why: "The service account lacks the right to read tax codes" },
];

const NO_API = "Sertica's Web API has no such list";
/** Account Info in Sertica's order (SPEC §8.14); Incident and PM/UPM Sub Value have no API. */
const ACCOUNT_ENTRIES: (Tab | Locked)[] = [
  "accounts",
  "approval-setups",
  { label: "Incident", why: NO_API },
  { label: "PM/UPM Sub Value", why: NO_API },
  "projects",
  "purchase-types",
  "technical-accounts",
  "vat",
];
/** Unit Info (SPEC §8.11). */
const UNIT_ENTRIES: (Tab | Locked)[] = [
  { label: "Business Activity", why: NO_API },
  { label: "Category", why: NO_API },
  "class-societies",
  "flags",
  { label: "KPI Group", why: NO_API },
  { label: "KPI Group Type", why: NO_API },
  "units",
  "unit-companies",
  "unit-groups",
  "vessel-types",
];
/** User Info (SPEC §8.12): each list for holders of its Sertica right. */
const userEntries = (rights: string[]): (Tab | Locked)[] => [
  { label: "Button Layout", why: NO_API },
  { label: "Group Mapping", why: NO_API },
  ...USER_TABS.map((t) => (rights.includes(USER_TAB_RIGHT[t]) ? t : { label: TAB_LABEL[t], why: `Your Sertica role lacks the ${USER_TAB_RIGHT[t]} right` })),
];

function useShell() {
  const { preset, me, tab, setSearch } = useDashboard();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>(getTheme());
  const choosePreset = (p: Preset) => {
    // A preset is a landing tab plus default filters; switching clears the rest.
    const cleared = Object.fromEntries(
      ["vessel", "type", "dept", "stage", "poStage", "flags", "urgent", "supplier", "region", "showDoNotUse", "approver", "project", "q", "purchaseType", "account", "age", "cycle"].map((k) => [k, undefined]),
    );
    setSearch({ ...cleared, ...presetDefaults(p, me), preset: p, tab: landingTab(p, me.canSeeBudgets) });
  };
  const cycleTheme = () => {
    const next: Theme = theme === "system" ? "dark" : theme === "dark" ? "light" : "system";
    applyTheme(next);
    setTheme(next);
  };
  const signOut = async () => {
    await api("/auth/sign-out", { method: "POST", body: "{}" });
    window.location.assign("/sign-in");
  };
  const go = (t: Tab) => setSearch({ tab: t });
  const userTabs: Tab[] = USER_TABS.filter((t) => me.userInfoRights.includes(USER_TAB_RIGHT[t]));
  const groups: Group[] = [
    { key: "procurement", label: "Procurement", icon: <CartIcon />, tabs: PROCUREMENT_TABS },
    { key: "budgeting", label: me.canSeeBudgets ? "Budgeting" : "🔒 Budgeting", icon: <WalletIcon />, tabs: ["budgets"] },
    { key: "address-book", label: "Address Book Info", icon: <BookIcon />, tabs: [...ADDRESS_TABS], entries: ADDRESS_ENTRIES },
    { key: "item-info", label: "Item Info", icon: <WrenchIcon />, tabs: [...ITEM_TABS], entries: ITEM_ENTRIES },
    { key: "master-data", label: "Master Data", icon: <DatabaseIcon />, tabs: [...MASTER_TABS] },
    { key: "account-info", label: "Account Info", icon: <AccountIcon />, tabs: [...ACCOUNT_TABS], entries: ACCOUNT_ENTRIES },
    { key: "unit-info", label: "Unit Info", icon: <ShipIcon />, tabs: [...UNIT_TABS], entries: UNIT_ENTRIES },
    // Personal data and rights: for roles holding Sertica's User, Role, UserGroup or UserRight right (SPEC §8.12).
    ...(userTabs.length ? [{ key: "user-info", label: "User Info", icon: <UserIcon />, tabs: userTabs, entries: userEntries(me.userInfoRights) }] : []),
    { key: "certificate-handling", label: "Certificate Handling", icon: <CertificateIcon />, tabs: [...CERTIFICATE_TABS] },
  ];
  // Procurement keeps the order of the purchase: requisition, RFQ, PO.
  const sorted = groups
    .map((g) => (g.key === "procurement" ? g : sortGroup(g)))
    .sort((a, b) => a.label.replace("🔒 ", "").localeCompare(b.label.replace("🔒 ", "")));
  const sections = sorted.map((g) => ({ ...g, active: g.tabs.includes(tab), go: () => !g.tabs.includes(tab) && go(g.tabs[0]!) }));
  return { preset, me, tab, theme, choosePreset, cycleTheme, signOut, sections, go, admin: () => void navigate({ to: "/admin" }) };
}

const themeLabel = (t: Theme) => (t === "system" ? "◐ Auto" : t === "dark" ? "☾ Dark" : "☀ Light");

function PresetSelect({ preset, onChange, className }: { preset: Preset; onChange: (p: Preset) => void; className: string }) {
  return (
    <label className="block">
      <span className="sr-only">Preset</span>
      <select value={preset} onChange={(e) => onChange(e.target.value as Preset)} className={className}>
        {PRESETS.map((p) => (
          <option key={p} value={p} className="text-black">
            {PRESET_LABEL[p]}
          </option>
        ))}
      </select>
    </label>
  );
}

const icon = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;
const CartIcon = () => (
  <svg {...icon}>
    <circle cx="9" cy="20" r="1.5" />
    <circle cx="18" cy="20" r="1.5" />
    <path d="M2 3h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L21 7H6" />
  </svg>
);
const WalletIcon = () => (
  <svg {...icon}>
    <rect x="2.5" y="5.5" width="19" height="14" rx="2" />
    <path d="M16 12.5h2M2.5 9.5h19" />
  </svg>
);
const BookIcon = () => (
  <svg {...icon}>
    <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H19v18H6.5A1.5 1.5 0 0 1 5 19.5z" />
    <circle cx="12" cy="10" r="2.5" />
    <path d="M8.5 16.5a3.5 3.5 0 0 1 7 0" />
  </svg>
);
const DatabaseIcon = () => (
  <svg {...icon}>
    <ellipse cx="12" cy="5.5" rx="8" ry="2.5" />
    <path d="M4 5.5v13c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-13M4 12c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5" />
  </svg>
);
const AccountIcon = () => (
  <svg {...icon}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18M7 15h4" />
  </svg>
);
const ShipIcon = () => (
  <svg {...icon}>
    <path d="M3 17l2 4h14l2-4H3zM6 17V9h12v8M9 9V5h6v4M12 3v2" />
  </svg>
);
const CertificateIcon = () => (
  <svg {...icon}>
    <circle cx="12" cy="9" r="5" />
    <path d="M9 13.5L7.5 21l4.5-2.5 4.5 2.5-1.5-7.5" />
  </svg>
);
const WrenchIcon = () => (
  <svg {...icon}>
    <path d="M14.7 6.3a4 4 0 0 0 5 5L21 13l-8 8-3-3 8-8-1.3-1.3a4 4 0 0 1-5-5L13 2.4" />
    <path d="M3 21l6-6" />
  </svg>
);
const ChevronIcon = ({ dir, size = 18 }: { dir: "left" | "right" | "up" | "down"; size?: number }) => (
  <svg {...icon} width={size} height={size}>
    <path d={{ left: "M15 6l-6 6 6 6", right: "M9 6l6 6-6 6", up: "M6 15l6-6 6 6", down: "M6 9l6 6 6-6" }[dir]} />
  </svg>
);

/** The navy side rail on laptop and desktop: each section opens to its tabs, Sertica-style.
 *  It collapses to section icons, remembered per browser. */
export function Sidebar({ freshness }: { freshness?: Freshness }) {
  const s = useShell();
  const [collapsed, setCollapsed] = useState(() => readPrefs().railCollapsed ?? false);
  // The active section starts open; others open on click.
  const [openKeys, setOpenKeys] = useState<string[]>(() => s.sections.filter((x) => x.active).map((x) => x.key));
  const toggle = () => {
    writePrefs({ railCollapsed: !collapsed });
    setCollapsed(!collapsed);
  };
  const toggleGroup = (key: string) => setOpenKeys((k) => (k.includes(key) ? k.filter((x) => x !== key) : [...k, key]));
  const stale = freshness && (!freshness.asOf || freshness.level !== "fresh");
  return (
    <aside
      className={`hidden lg:flex flex-col shrink-0 bg-navy text-white sticky top-0 h-screen transition-[width] duration-150 ${collapsed ? "w-14" : "w-[180px]"}`}
      aria-label="Side rail"
    >
      <div className={`flex items-center h-11 border-b border-white/10 ${collapsed ? "justify-center" : "px-3"}`}>
        {!collapsed && (
          <a href="/" className="font-black text-[15px] tracking-[0.06em] flex-1" title="SOECHI">
            SOECHI
          </a>
        )}
        <button
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand side rail" : "Collapse side rail"}
          title={collapsed ? "Expand" : "Collapse"}
          className="rounded p-1 text-slate-300 hover:bg-white/10 hover:text-white"
        >
          <MenuIcon />
        </button>
      </div>
      <nav aria-label="Sections" className={`flex flex-col py-2 overflow-y-auto ${collapsed ? "items-center gap-1" : ""}`}>
        {s.sections.map((x) =>
          collapsed ? (
            <button
              key={x.key}
              onClick={x.go}
              aria-current={x.active ? "page" : undefined}
              aria-label={x.label}
              title={x.label}
              className={`flex items-center justify-center w-10 h-9 rounded ${x.active ? "bg-rail-group text-rail-hit" : "text-slate-200 hover:bg-white/5"}`}
            >
              {x.icon}
            </button>
          ) : (
            <div key={x.key} className={openKeys.includes(x.key) ? "bg-rail-group" : ""}>
              <button
                onClick={() => toggleGroup(x.key)}
                aria-expanded={openKeys.includes(x.key)}
                className="w-full flex items-center gap-2.5 px-3 h-8 text-[12.5px] font-medium text-slate-100 hover:bg-white/5"
              >
                <span className="text-slate-200">{x.icon}</span>
                <span className="flex-1 text-left truncate">{x.label}</span>
                <ChevronIcon dir={openKeys.includes(x.key) ? "up" : "down"} size={14} />
              </button>
              {openKeys.includes(x.key) && (
                <ul className="bg-rail-sub py-1">
                  {(x.entries ?? x.tabs).map((t) =>
                    typeof t !== "string" ? (
                      <li key={t.label}>
                        <span title={t.why} aria-disabled className="block pl-10 pr-3 leading-7 text-[12px] font-medium truncate text-slate-400 cursor-not-allowed">
                          {t.label}
                        </span>
                      </li>
                    ) : (
                    <li key={t}>
                      <button
                        onClick={() => s.go(t)}
                        aria-current={s.tab === t ? "page" : undefined}
                        className={`w-full text-left pl-10 pr-3 h-7 text-[12px] font-medium truncate hover:bg-white/5 ${s.tab === t ? "text-rail-hit" : "text-slate-100"}`}
                      >
                        {TAB_LABEL[t]}
                      </button>
                    </li>
                    ),
                  )}
                </ul>
              )}
            </div>
          ),
        )}
      </nav>
      <div className="flex-1" />
      {collapsed ? (
        // Collapsed: only a warning when the data is stale; expand for the rest.
        stale && (
          <span className="text-center text-amber-300 pb-4" title="Data may be out of date">
            ⚠
          </span>
        )
      ) : (
      <div className="space-y-2 px-3 py-3 text-[12px] border-t border-white/10">
        <PresetSelect preset={s.preset} onChange={s.choosePreset} className="w-full bg-white/10 border border-white/20 rounded-input px-2 py-1 text-[12px] text-white" />
        <FreshnessIndicator f={freshness} />
      </div>
      )}
    </aside>
  );
}

/** The white bar over every page: the tab's title on the left; vessel, theme and the user's menu on the right. */
export function TopBar({ children }: { children?: ReactNode }) {
  const s = useShell();
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setMenu(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);
  return (
    <div className="hidden lg:flex items-center gap-3 h-11 px-4 border-b border-line bg-card">
      <span className="text-brand-2" aria-hidden>
        <ListIcon />
      </span>
      <h1 className="text-[14px] font-bold uppercase tracking-[0.02em] text-ink flex-1 truncate">{TAB_LABEL[s.tab]}</h1>
      {children}
      <SyncButton tab={s.tab} />
      <button onClick={s.cycleTheme} className="ghost" title={`Theme: ${themeLabel(s.theme)} (light / dark / system)`} aria-label={`Theme: ${themeLabel(s.theme)}`}>
        <span aria-hidden className="text-[15px] leading-none">{themeLabel(s.theme).split(" ")[0]}</span>
      </button>
      <div className="relative" ref={ref}>
        <button className="ghost" aria-expanded={menu} aria-label={`Account: ${s.me.roleName ? `${s.me.name} · ${s.me.roleName}` : s.me.name}`} title={s.me.roleName ? `${s.me.name} · ${s.me.roleName}` : s.me.name} onClick={() => setMenu((m) => !m)}>
          <UserIcon />
          <span aria-hidden className="text-[9px] text-muted">▼</span>
        </button>
        {menu && (
          <div className="card absolute right-0 z-30 mt-1 w-56 shadow-lg py-1 text-[13px]">
            <div className="px-3 py-1.5 border-b border-line mb-1">
              <div className="text-muted truncate">{s.me.name}</div>
              {s.me.roleName && (
                <div className="text-[12px] text-ink truncate" title={`Signed in as ${s.me.roleName}`}>
                  {s.me.roleName}
                </div>
              )}
            </div>
            {s.me.isAdmin && (
              <button onClick={s.admin} className="block w-full text-left px-3 py-1.5 hover:bg-hover">
                Admin
              </button>
            )}
            <button onClick={s.signOut} className="block w-full text-left px-3 py-1.5 hover:bg-hover">
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Sync (ARCHITECTURE §6): fetches what changed in Sertica for the active page only and reloads the
 * page's data; while it runs it is the Stop button. With scheduled sync off, this, a search and a
 * drawer's refresh are the only ways data gets current.
 */
function SyncButton({ tab, dark }: { tab: Tab; dark?: boolean }) {
  const qc = useQueryClient();
  const scope = syncScopeOf(tab);
  const s = useSyncNow(() => void qc.invalidateQueries(), "/sync", { scope });
  const page = scope === "budgets" ? "Budgets" : scope === "procurement" ? "Procurement" : tabSection(tab)?.label ?? TAB_LABEL[tab];
  // Opening the page syncs its part too (lib/page-sync.ts): shown here while the button is idle.
  const onOpen = usePageSyncState();
  const opening = !s.busy && s.state.kind === "idle" && onOpen.status === "syncing";
  const status =
    s.state.kind === "failed" ? s.state.message
    : s.state.kind === "done" ? "Synced"
    : s.state.kind === "stopped" ? "Sync stopped"
    : s.busy ? null
    : onOpen.status === "unavailable" ? "The sync service is not running."
    : onOpen.status === "failed" ? "Could not update this page from Sertica."
    : onOpen.status === "done" ? "Updated from Sertica"
    : null;
  const failed = s.state.kind === "failed" || (s.state.kind === "idle" && (onOpen.status === "failed" || onOpen.status === "unavailable"));
  const tone = failed ? (dark ? "text-red-300" : "text-[var(--danger)]") : dark ? "text-slate-300" : "text-muted";
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      {s.busy && <ProgressBar progress={s.progress} className={`w-[220px] ${dark ? "[&_.text-muted]:text-slate-300" : ""}`} />}
      {opening && onOpen.status === "syncing" && (
        <ProgressBar progress={onOpen.progress} label="Updating this page from Sertica…" className={`w-[220px] ${dark ? "[&_.text-muted]:text-slate-300" : ""}`} />
      )}
      {status && (
        <span role="status" className={`text-[12px] truncate max-w-[220px] ${tone}`} title={status}>
          {(s.state.kind === "done" || (s.state.kind === "idle" && onOpen.status === "done")) && "✓ "}
          {status}
        </span>
      )}
      <button
        onClick={s.busy ? s.stop : s.start}
        className={dark ? "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] font-semibold bg-white/10" : "ghost"}
        title={s.busy ? "Stop the sync; what it has fetched so far stays" : `Fetch what changed in Sertica for ${page}`}
      >
        {s.busy ? (
          <svg aria-hidden viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="1.5" />
          </svg>
        ) : (
          <svg aria-hidden viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v6h-6" />
          </svg>
        )}
        {s.busy ? "Stop" : "Sync"}
      </button>
    </span>
  );
}

const ListIcon = () => (
  <svg {...icon}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);
const MenuIcon = () => (
  <svg {...icon}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);
const UserIcon = () => (
  <svg {...icon} width={16} height={16}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

/** Below 1024 px the rail folds into a navy top bar. */
export function MobileBar({ freshness }: { freshness?: Freshness }) {
  const s = useShell();
  return (
    <header className="lg:hidden bg-navy text-white px-4 py-2.5 space-y-2">
      <div className="flex items-center gap-3">
        <a href="/" className="font-extrabold text-[15px]">
          SOECHI
        </a>
        <nav aria-label="Sections" className="flex gap-1">
          {s.sections.map((x) => (
            <button
              key={x.key}
              onClick={x.go}
              aria-current={x.active ? "page" : undefined}
              className={`rounded-md px-2 py-1 text-[13px] font-semibold ${x.active ? "bg-white/15" : "text-slate-300"}`}
            >
              {x.label}
            </button>
          ))}
        </nav>
        <div className="flex-1" />
        <SyncButton tab={s.tab} dark />
        <button onClick={s.signOut} className="text-[12.5px] text-slate-300">
          Sign out
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-300">
        <PresetSelect preset={s.preset} onChange={s.choosePreset} className="bg-white/10 border border-white/20 rounded-input px-2 py-0.5 text-[12.5px] text-white" />
        <FreshnessIndicator f={freshness} />
        <button onClick={s.cycleTheme}>{themeLabel(s.theme)}</button>
        {s.me.isAdmin && <button onClick={s.admin}>Admin</button>}
      </div>
    </header>
  );
}
